import HttpService from '@pingum/api-services/HttpService';

export default class TagReminderService {
  static getTagReminders(status, eventTemplateReminderId, page, size, sort, options = {}) {
    if (Array.isArray(options.tagIds) && options.tagIds.length === 0) {
      return Promise.resolve({
        content: [],
        totalElements: 0,
        totalPages: 0,
        number: page || 0,
        size: size || 25,
        first: true,
        last: true,
        empty: true,
      });
    }
    const params = new URLSearchParams({
      status: status || '',
      eventTemplateReminderId: eventTemplateReminderId || '',
      page: page || 0,
      size: size || 25,
    });
    if (options.tagIds) { params.set('tagIds', options.tagIds.join(',')); }
    if (options.maxDueDate) { params.set('maxDueDate', options.maxDueDate); }
    if (options.needsScheduling) { params.set('needsScheduling', 'true'); }
    // Existing callers pass either a sort string or an array of sort strings.
    const sorting = Array.isArray(sort)
      ? sort.join('')
      : sort || '';
    new URLSearchParams(sorting).getAll('sort').forEach((value) => params.append('sort', value));
    // The shared get(url) encodes URLs again; let its configured Axios client encode query values once.
    return this.httpService.http.get('', { params });
  }

  static async getAllTagReminders(options = {}) {
    const filters = { ...options };
    if (Array.isArray(filters.tagIds)) {
      filters.tagIds = [...new Set(filters.tagIds.map(Number).filter((id) => Number.isSafeInteger(id) && id > 0))];
      if (filters.tagIds.length === 0) return [];
    }
    const reminders = [];
    let page = 0;
    let totalPages = 1;
    let totalElements = null;
    let pageSize = null;
    let lastId = 0;
    const incompleteMessage = 'Horse reminders could not be loaded completely. Please retry.';
    do {
      // Each page depends on the previous response's pagination checks.
      // eslint-disable-next-line no-await-in-loop
      const response = await this.getTagReminders(filters.status, filters.eventTemplateReminderId, page, 100, '&sort=id,asc', filters);
      if (!response || !Array.isArray(response.content)
        || response.number !== page || !Number.isSafeInteger(response.size) || response.size <= 0
        || !Number.isSafeInteger(response.totalPages) || response.totalPages < 0
        || !Number.isSafeInteger(response.totalElements) || response.totalElements < 0) {
        throw new Error(incompleteMessage);
      }
      const expectedPages = Math.ceil(response.totalElements / response.size);
      const expectedLength = Math.min(response.size, Math.max(response.totalElements - (page * response.size), 0));
      const last = page >= response.totalPages - 1;
      if (response.totalPages !== expectedPages || response.content.length !== expectedLength
        || (response.last != null && response.last !== last)
        || (response.numberOfElements != null && response.numberOfElements !== response.content.length)
        || (totalElements !== null && (totalElements !== response.totalElements || pageSize !== response.size))) {
        throw new Error(incompleteMessage);
      }
      const lastPageId = lastId;
      const invalidIds = response.content.some((reminder, index, entries) => {
        const id = Number(reminder?.id);
        const previousId = index > 0
          ? Number(entries[index - 1]?.id)
          : lastPageId;
        return !Number.isSafeInteger(id) || id <= previousId;
      });
      if (invalidIds) throw new Error(incompleteMessage);
      reminders.push(...response.content);
      if (response.content.length > 0) lastId = Number(response.content[response.content.length - 1].id);
      totalPages = response.totalPages;
      totalElements = response.totalElements;
      pageSize = response.size;
      page += 1;
    } while (page < totalPages);
    return reminders;
  }

  static getTagReminder(tagReminderId) {
    return this.httpService.get(`/${tagReminderId}`);
  }

  static getTagReminderEvents(tagReminderId) {
    return this.httpService.get(`/${tagReminderId}/events`);
  }

  static getTagRemindersForDueDate(tagIds, maxDueDate) {
    const tags = (tagIds || []).filter((id) => Number.isInteger(Number(id)) && Number(id) > 0);
    if (tags.length === 0) { return Promise.resolve([]); }
    const params = new URLSearchParams({
      tagIds: [...new Set(tags)].join(','),
      maxDueDate: (maxDueDate && maxDueDate.toISOString()) || '',
    });
    return this.httpService.http.get('/dueDates', { params });
  }
}

TagReminderService.httpService = new HttpService({
  baseURL: `${typeof window !== 'undefined'
    ? env.REACT_APP_EVENT_API
    : ''}/tagReminders`,
});
